import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ShoppingCart,
  Plus,
  Loader2,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  Search,
  PackagePlus,
  Check,
  ChevronsUpDown,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuickOrderDialog({ 
  open, 
  onOpenChange, 
  suppliers = [],
  exchangeRate = 11.5,
  preselectedProduct = null,
  onOrderCreated,
  navigateToOrdersAfter = false
}) {
  const navigate = useNavigate();
  
  // Product search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Selected product and form
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [orderForm, setOrderForm] = useState({
    quantity: 1,
    supplier_id: "",
    expected_week: null
  });
  const [loading, setLoading] = useState(false);
  
  // Missing price warning
  const [showMissingPriceWarning, setShowMissingPriceWarning] = useState(false);
  const [missingPriceProduct, setMissingPriceProduct] = useState(null);
  
  // Product not found state
  const [showProductNotFound, setShowProductNotFound] = useState(false);
  const [searchedQuery, setSearchedQuery] = useState("");
  
  // Already ordered warning
  const [showOrderedWarning, setShowOrderedWarning] = useState(false);
  const [orderedWarningData, setOrderedWarningData] = useState(null);
  const [pendingProduct, setPendingProduct] = useState(null);
  
  // Create product dialog state
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [createProductForm, setCreateProductForm] = useState({
    artikelnummer: "",
    ean: "",
    kategori: "",
    tillverkare: ""
  });
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [manufacturerOpen, setManufacturerOpen] = useState(false);
  
  // Add price dialog state
  const [showAddPriceDialog, setShowAddPriceDialog] = useState(false);
  const [addPriceForm, setAddPriceForm] = useState({
    supplier_id: "",
    price: "",
    currency: "SEK",
    chemical_tax: "0"
  });
  const [savingPrice, setSavingPrice] = useState(false);

  // Fetch categories and manufacturers when dialog opens
  useEffect(() => {
    if (open && categories.length === 0) {
      fetchFilters();
    }
  }, [open]);
  
  const fetchFilters = async () => {
    try {
      const [catRes, mfgRes] = await Promise.all([
        axios.get(`${API}/inventory/categories`),
        axios.get(`${API}/inventory/manufacturers`)
      ]);
      setCategories(catRes.data || []);
      setManufacturers(mfgRes.data || []);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  // Initialize with preselected product when dialog opens
  useEffect(() => {
    if (open && preselectedProduct) {
      // If preselectedProduct has a searchQuery (from empty filter state), just fill the search field
      if (preselectedProduct.searchQuery) {
        setSearchQuery(preselectedProduct.searchQuery);
        // The search effect will handle finding the product or showing "not found"
      }
      // If preselectedProduct only has artikelnummer (from return flow), search for it first
      else if (preselectedProduct.artikelnummer && !preselectedProduct.ean) {
        setSearchQuery(preselectedProduct.artikelnummer);
        // The search effect will handle finding the product
      } else {
        setupPreselectedProduct(preselectedProduct);
      }
    } else if (!open) {
      // Reset state when dialog closes
      setSearchQuery("");
      setSearchResults([]);
      setSelectedProduct(null);
      setOrderForm({ quantity: 1, supplier_id: "", expected_week: getCurrentWeek() });
      setShowMissingPriceWarning(false);
      setShowOrderedWarning(false);
      setOrderedWarningData(null);
      setPendingProduct(null);
      setShowProductNotFound(false);
      setSearchedQuery("");
      setShowCreateProduct(false);
      setCreateProductForm({ artikelnummer: "", ean: "", kategori: "", tillverkare: "" });
      setShowAddPriceDialog(false);
      setAddPriceForm({ supplier_id: "", price: "", currency: "SEK", chemical_tax: "0" });
    }
  }, [open, preselectedProduct]);

  // Get current week number
  const getCurrentWeek = () => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const days = Math.floor((now - startOfYear) / (24 * 60 * 60 * 1000));
    return Math.ceil((days + startOfYear.getDay() + 1) / 7);
  };

  // Setup preselected product with price info
  const setupPreselectedProduct = async (product) => {
    setSearchQuery(product.artikelnummer || product.ean || "");
    
    // If product already has supplier info from PricesPage, use it directly
    if (product.supplierId && product.price !== undefined) {
      const supplierInfo = suppliers.find(s => s.id === product.supplierId);
      const supplierPrices = [{
        supplier_id: product.supplierId,
        supplier_name: supplierInfo?.company_name || product.supplierName || "Okänd",
        price: product.price,
        currency: product.currency || "SEK"
      }];
      
      setSelectedProduct({
        ...product,
        supplierPrices,
        bestSupplier: supplierPrices[0]
      });
      setOrderForm({
        quantity: 1,
        supplier_id: product.supplierId,
        expected_week: getCurrentWeek()
      });
      
      // Fetch additional supplier prices in background (parallel)
      fetchAllSupplierPrices(product, supplierPrices);
      return;
    }
    
    // No pre-existing price info - show product immediately, fetch prices in background
    setSelectedProduct({
      ...product,
      supplierPrices: [],
      bestSupplier: null
    });
    setOrderForm({
      quantity: 1,
      supplier_id: suppliers[0]?.id || "",
      expected_week: getCurrentWeek()
    });
    
    // Fetch all supplier prices in parallel
    fetchAllSupplierPrices(product, []);
  };
  
  // Fetch supplier prices in parallel (background)
  const fetchAllSupplierPrices = async (product, existingPrices) => {
    try {
      const pricePromises = suppliers.map(async (supplier) => {
        try {
          const priceRes = await axios.get(`${API}/purchasing/suppliers/${supplier.id}/prices`, {
            params: { search: product.ean }
          });
          const match = priceRes.data.find(p => p.ean === product.ean);
          if (match) {
            return {
              supplier_id: supplier.id,
              supplier_name: supplier.company_name,
              price: match.purchase_price,
              currency: match.currency
            };
          }
        } catch (e) {
          // Ignore individual failures
        }
        return null;
      });
      
      const results = await Promise.all(pricePromises);
      const supplierPrices = results.filter(Boolean);
      
      // Merge with existing prices (avoid duplicates)
      const existingIds = new Set(existingPrices.map(p => p.supplier_id));
      const allPrices = [
        ...existingPrices,
        ...supplierPrices.filter(p => !existingIds.has(p.supplier_id))
      ];
      
      // Sort by price (lowest first)
      allPrices.sort((a, b) => a.price - b.price);
      
      // Update product with all prices
      setSelectedProduct(prev => prev ? {
        ...prev,
        supplierPrices: allPrices,
        bestSupplier: allPrices[0] || null
      } : null);
      
      // Auto-select best supplier (lowest price) if prices exist
      if (allPrices.length > 0) {
        setOrderForm(prev => {
          // Only auto-select if no supplier selected yet, or if current selection has no price
          const currentHasPrice = allPrices.some(p => p.supplier_id === prev.supplier_id);
          if (!prev.supplier_id || !currentHasPrice) {
            return { ...prev, supplier_id: allPrices[0].supplier_id };
          }
          return prev;
        });
      }
    } catch (e) {
      console.error("Error fetching supplier prices:", e);
    }
  };

  // Search products when query changes
  useEffect(() => {
    // Don't search if dialog is closed
    if (!open) return;
    
    // Don't search if we have a full preselected product (with ean), but allow if it's just a searchQuery
    if (preselectedProduct && preselectedProduct.ean) {
      return;
    }
    
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setShowProductNotFound(false);
      return;
    }
    
    const timer = setTimeout(async () => {
      setSearching(true);
      setShowProductNotFound(false);
      try {
        const response = await axios.get(`${API}/inventory`, {
          params: { search: searchQuery.trim(), limit: 10 }
        });
        const items = response.data.items || [];
        setSearchResults(items);
        
        // Show "product not found" if no results and query is substantial
        if (items.length === 0 && searchQuery.trim().length >= 3) {
          setSearchedQuery(searchQuery.trim());
          setShowProductNotFound(true);
        } else {
          setShowProductNotFound(false);
        }
      } catch (error) {
        console.error("Error searching products:", error);
      } finally {
        setSearching(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery, open, preselectedProduct]);

  // Select a product from search results
  const selectProduct = async (product) => {
    setSearchQuery(product.artikelnummer || product.ean);
    setSearchResults([]);
    
    // Check if product is already ordered
    try {
      const response = await axios.get(`${API}/purchasing/check-ordered/${product.ean}`);
      if (response.data.is_ordered) {
        // Show warning dialog
        setPendingProduct(product);
        setOrderedWarningData({
          orderNumber: response.data.order_number,
          supplierName: response.data.supplier_name,
          status: response.data.status,
          quantity: response.data.quantity,
          receivedQuantity: response.data.received_quantity
        });
        setShowOrderedWarning(true);
        return;
      }
    } catch (error) {
      console.error("Error checking if ordered:", error);
      // Continue anyway if check fails
    }
    
    // Check if product has any prices BEFORE showing order form
    await checkPricesAndSetupProduct(product);
  };
  
  // Check prices (for suggestions) and set up the order form. Never blocks:
  // a line can be created without a supplier or price (chosen later at inquiry).
  const checkPricesAndSetupProduct = async (product) => {
    // If product already has supplier info from PricesPage, use it directly
    if (product.supplierId && product.price !== undefined) {
      await setupPreselectedProduct(product);
      return;
    }

    let supplierPrices = [];
    try {
      const pricePromises = suppliers.map(async (supplier) => {
        try {
          const priceRes = await axios.get(`${API}/purchasing/suppliers/${supplier.id}/prices`, {
            params: { search: product.ean }
          });
          const match = priceRes.data.find(p => p.ean === product.ean);
          if (match) {
            return {
              supplier_id: supplier.id,
              supplier_name: supplier.company_name,
              price: match.purchase_price,
              currency: match.currency
            };
          }
        } catch (e) {
          // Ignore individual failures
        }
        return null;
      });
      const results = await Promise.all(pricePromises);
      supplierPrices = results.filter(Boolean);
      supplierPrices.sort((a, b) => a.price - b.price);
    } catch (e) {
      console.error("Error checking prices:", e);
    }

    setSelectedProduct({
      ...product,
      supplierPrices,
      bestSupplier: supplierPrices[0] || null
    });
    // Default to NO supplier ("välj senare") so the user is never forced to choose here
    setOrderForm({
      quantity: 1,
      supplier_id: "",
      expected_week: getCurrentWeek()
    });
  };
  
  // Continue with order despite already being ordered
  const handleContinueOrder = async () => {
    setShowOrderedWarning(false);
    if (pendingProduct) {
      await checkPricesAndSetupProduct(pendingProduct);
      setPendingProduct(null);
    }
  };
  
  // Go to orders page
  const handleGoToOrders = () => {
    setShowOrderedWarning(false);
    onOpenChange(false);
    navigate("/purchasing/orders");
  };

  // Submit the order (supplier + price are optional here)
  const handleSubmit = async () => {
    if (!selectedProduct) {
      toast.error("Välj en produkt");
      return;
    }
    if (orderForm.quantity < 1) {
      toast.error("Ange antal");
      return;
    }
    const supplierId = orderForm.supplier_id && orderForm.supplier_id !== "__none__"
      ? orderForm.supplier_id : null;
    const priceInfo = supplierId
      ? selectedProduct.supplierPrices?.find(p => p.supplier_id === supplierId)
      : null;
    await submitOrder(priceInfo, supplierId);
  };

  // Actually submit the order
  const submitOrder = async (priceInfo, supplierId) => {
    setLoading(true);
    try {
      await axios.post(`${API}/purchasing/orders`, {
        supplier_id: supplierId || null,
        expected_delivery_week: null,
        items: [{
          ean: selectedProduct.ean,
          artikelnummer: selectedProduct.artikelnummer,
          quantity: orderForm.quantity,
          unit_price: priceInfo?.price || 0,
          currency: priceInfo?.currency || "SEK"
        }]
      });

      toast.success(`Tillagd att beställa: ${orderForm.quantity} st ${selectedProduct.artikelnummer}`);
      onOpenChange(false);
      onOrderCreated?.();

      if (navigateToOrdersAfter) {
        navigate("/purchasing/orders");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa beställning");
    } finally {
      setLoading(false);
    }
  };

  // Continue order despite missing price
  const handleContinueWithoutPrice = async () => {
    setShowMissingPriceWarning(false);
    
    // If we don't have a selectedProduct yet (came directly from create product flow),
    // we need to set up the product first so user can select supplier and quantity
    if (!selectedProduct && missingPriceProduct) {
      // Setup the product without prices
      setSelectedProduct({
        ...missingPriceProduct,
        supplierPrices: [],
        bestSupplier: null
      });
      setSearchQuery(missingPriceProduct.artikelnummer);
      setOrderForm({
        quantity: 1,
        supplier_id: suppliers[0]?.id || "",
        expected_week: getCurrentWeek()
      });
      // User will now see the order form and can proceed
      return;
    }
    
    // Normal flow - already have selected product, just submit without price
    await submitOrder(null);
  };

  // Open add price dialog
  const handleOpenAddPriceDialog = async () => {
    // Check if product already has chemical_tax
    let existingChemicalTax = "";
    if (missingPriceProduct?.ean) {
      try {
        const response = await axios.get(`${API}/purchasing/prices/by-ean/${missingPriceProduct.ean}`);
        const prices = response.data || [];
        // Find any existing chemical_tax
        const priceWithTax = prices.find(p => p.chemical_tax);
        if (priceWithTax) {
          existingChemicalTax = priceWithTax.chemical_tax.toString();
        }
      } catch (error) {
        // Ignore errors
      }
    }
    
    setAddPriceForm({
      supplier_id: suppliers[0]?.id || "",
      price: "",
      currency: "SEK",
      chemical_tax: existingChemicalTax
    });
    setShowMissingPriceWarning(false);
    setShowAddPriceDialog(true);
  };
  
  // Save price and continue with order
  const handleSavePrice = async () => {
    if (!addPriceForm.supplier_id) {
      toast.error("Välj leverantör");
      return;
    }
    if (!addPriceForm.price || parseFloat(addPriceForm.price) <= 0) {
      toast.error("Ange ett giltigt pris");
      return;
    }
    
    setSavingPrice(true);
    try {
      const product = missingPriceProduct;
      await axios.post(`${API}/purchasing/prices`, {
        supplier_id: addPriceForm.supplier_id,
        ean: product.ean,
        purchase_price: parseFloat(addPriceForm.price),
        currency: addPriceForm.currency,
        chemical_tax: addPriceForm.chemical_tax ? parseFloat(addPriceForm.chemical_tax) : null
      });
      
      toast.success("Pris sparat!");
      setShowAddPriceDialog(false);
      
      // Now setup the product with the new price and show order form
      const supplierInfo = suppliers.find(s => s.id === addPriceForm.supplier_id);
      setSelectedProduct({
        ...product,
        supplierPrices: [{
          supplier_id: addPriceForm.supplier_id,
          supplier_name: supplierInfo?.company_name || "Okänd",
          price: parseFloat(addPriceForm.price),
          currency: addPriceForm.currency
        }],
        bestSupplier: {
          supplier_id: addPriceForm.supplier_id,
          supplier_name: supplierInfo?.company_name || "Okänd",
          price: parseFloat(addPriceForm.price),
          currency: addPriceForm.currency
        }
      });
      setSearchQuery(product.artikelnummer);
      setOrderForm({
        quantity: 1,
        supplier_id: addPriceForm.supplier_id,
        expected_week: getCurrentWeek()
      });
      
    } catch (error) {
      console.error("Error saving price:", error);
      toast.error(error.response?.data?.detail || "Kunde inte spara pris");
    } finally {
      setSavingPrice(false);
    }
  };

  // Open create product dialog with prefilled data
  const handleOpenCreateProduct = () => {
    // Check if searchedQuery looks like an EAN (8-13 digits)
    const isEan = /^\d{8,13}$/.test(searchedQuery);
    setCreateProductForm({
      artikelnummer: isEan ? "" : searchedQuery.toUpperCase(),
      ean: isEan ? searchedQuery : "",
      kategori: "",
      tillverkare: ""
    });
    setShowCreateProduct(true);
  };
  
  // Create the product
  const handleCreateProduct = async () => {
    if (!createProductForm.artikelnummer?.trim()) {
      toast.error("Ange artikelnummer");
      return;
    }
    if (!createProductForm.ean?.trim()) {
      toast.error("Ange EAN");
      return;
    }
    
    setCreatingProduct(true);
    try {
      // Create the product in inventory
      await axios.post(`${API}/inventory`, {
        ean: createProductForm.ean,
        artikelnummer: createProductForm.artikelnummer,
        tillverkare: createProductForm.tillverkare || "",
        kategori: createProductForm.kategori || "",
        antal: 0,
        lagerplats: ""
      });
      
      toast.success(`Produkt ${createProductForm.artikelnummer} skapad!`);
      setShowCreateProduct(false);
      setShowProductNotFound(false);
      
      // The newly created product has no price - set it up directly (supplier optional)
      const newProduct = {
        ean: createProductForm.ean,
        artikelnummer: createProductForm.artikelnummer,
        produktnamn: createProductForm.artikelnummer,
        tillverkare: createProductForm.tillverkare,
        kategori: createProductForm.kategori
      };

      setSelectedProduct({ ...newProduct, supplierPrices: [], bestSupplier: null });
      setSearchQuery(newProduct.artikelnummer);
      setOrderForm({ quantity: 1, supplier_id: "", expected_week: getCurrentWeek() });

      // Reset the create form
      setCreateProductForm({ artikelnummer: "", ean: "", kategori: "", tillverkare: "" });
      
    } catch (error) {
      console.error("Error creating product:", error);
      toast.error(error.response?.data?.detail || "Kunde inte skapa produkt");
    } finally {
      setCreatingProduct(false);
    }
  };

  return (
    <>
      <Dialog open={open && !showMissingPriceWarning && !showCreateProduct} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-blue-500" />
              Snabbeställning
            </DialogTitle>
            <DialogDescription>
              {selectedProduct 
                ? `Skapa beställning för ${selectedProduct.artikelnummer}`
                : "Sök efter en produkt att beställa"
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Product search (if no preselected product) */}
            {!selectedProduct && (
              <div className="space-y-2">
                <Label>Sök produkt</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="EAN eller artikelnummer..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="quick-order-search"
                  />
                  {searching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin" />
                  )}
                </div>
                
                {/* Search results */}
                {searchResults.length > 0 && (
                  <div className="border rounded-md max-h-48 overflow-y-auto">
                    {searchResults.map(product => (
                      <div
                        key={product.ean}
                        className="p-2 hover:bg-muted cursor-pointer border-b last:border-b-0"
                        onClick={() => selectProduct(product)}
                        data-testid={`search-result-${product.ean}`}
                      >
                        <p className="font-mono font-medium">{product.artikelnummer}</p>
                        <p className="text-xs text-muted-foreground">{product.ean}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Product not found - offer to create new */}
                {showProductNotFound && !searching && (
                  <div className="border border-amber-500/30 bg-amber-500/5 rounded-md p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium text-amber-600">Produkten hittades inte</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          "{searchedQuery}" finns inte i lagersystemet.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3 gap-2"
                          onClick={handleOpenCreateProduct}
                          data-testid="create-product-from-search-btn"
                        >
                          <Plus className="h-4 w-4" />
                          Skapa ny produkt
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Selected product info */}
            {selectedProduct && (
              <>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-mono font-bold text-lg">{selectedProduct.artikelnummer}</p>
                  <p className="text-sm text-muted-foreground font-mono">{selectedProduct.ean}</p>
                  {selectedProduct.produktnamn && (
                    <p className="text-sm mt-1">{selectedProduct.produktnamn}</p>
                  )}
                </div>
                
                {/* Quantity */}
                <div className="space-y-2">
                  <Label>Antal *</Label>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setOrderForm(prev => ({ ...prev, quantity: Math.max(1, prev.quantity - 1) }))}
                    >
                      -
                    </Button>
                    <Input
                      type="number"
                      min={1}
                      value={orderForm.quantity}
                      onChange={(e) => setOrderForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                      className="w-20 text-center font-bold text-lg"
                      data-testid="quick-order-quantity"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setOrderForm(prev => ({ ...prev, quantity: prev.quantity + 1 }))}
                    >
                      +
                    </Button>
                  </div>
                </div>
                
                {/* Supplier & delivery week are chosen later, at the inquiry / ordered stage */}
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={loading || !selectedProduct || orderForm.quantity < 1}
              className="gap-2"
              data-testid="quick-order-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Skapar...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Lägg till att beställa
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Missing price warning dialog */}
      <Dialog open={showMissingPriceWarning} onOpenChange={setShowMissingPriceWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Produkten saknar pris
            </DialogTitle>
            <DialogDescription className="pt-2">
              <span className="font-medium">{missingPriceProduct?.artikelnummer}</span> saknar pris hos vald leverantör. 
              Lägg till pris först för att statistiken ska uppdateras korrekt.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <p>Utan pris kan inte inköpsvärde och marginaler beräknas korrekt i statistiken.</p>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={handleContinueWithoutPrice}
              className="w-full sm:w-auto"
            >
              Fortsätt ändå
            </Button>
            <Button 
              onClick={handleOpenAddPriceDialog}
              className="w-full sm:w-auto gap-2"
            >
              <Plus className="h-4 w-4" />
              Lägg till pris
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Already ordered warning dialog */}
      <Dialog open={showOrderedWarning} onOpenChange={setShowOrderedWarning}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <AlertTriangle className="h-5 w-5" />
              Produkten är redan beställd
            </DialogTitle>
            <DialogDescription>
              Produkten finns redan i en aktiv beställning.
            </DialogDescription>
          </DialogHeader>
          {pendingProduct && orderedWarningData && (
            <div className="space-y-3">
              <p className="text-sm">
                <strong>{pendingProduct.artikelnummer || pendingProduct.ean}</strong> finns redan i en aktiv beställning.
              </p>
              <div className="bg-muted/50 rounded-lg p-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Order:</span>
                  <span className="font-medium">{orderedWarningData.orderNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Leverantör:</span>
                  <span>{orderedWarningData.supplierName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Antal:</span>
                  <span>{orderedWarningData.quantity} st</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="inline-flex items-center rounded-md bg-yellow-400/10 px-2 py-1 text-xs font-medium text-yellow-500 ring-1 ring-inset ring-yellow-400/20">
                    {orderedWarningData.status === 'pending' ? 'Ska beställas' : 
                     orderedWarningData.status === 'ordered' ? 'Beställd' : 
                     orderedWarningData.status === 'in_transit' ? 'På väg' : orderedWarningData.status}
                  </span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Vill du skapa en ny beställning ändå, eller gå till den befintliga?
              </p>
            </div>
          )}
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button 
              variant="outline" 
              onClick={handleGoToOrders}
            >
              Gå till beställningar
            </Button>
            <Button 
              onClick={handleContinueOrder}
            >
              Beställ ändå
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Product Dialog */}
      <Dialog open={showCreateProduct} onOpenChange={setShowCreateProduct}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackagePlus className="h-5 w-5 text-green-500" />
              Skapa ny produkt
            </DialogTitle>
            <DialogDescription>
              Fyll i produktuppgifterna för att skapa en ny produkt i systemet
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Artikelnummer *</Label>
                <Input
                  placeholder="T.ex. ABC-123"
                  value={createProductForm.artikelnummer}
                  onChange={(e) => setCreateProductForm(prev => ({ ...prev, artikelnummer: e.target.value.toUpperCase() }))}
                  data-testid="create-product-artikelnummer"
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label>EAN *</Label>
                <Input
                  placeholder="8-13 siffror"
                  value={createProductForm.ean}
                  onChange={(e) => setCreateProductForm(prev => ({ ...prev, ean: e.target.value }))}
                  data-testid="create-product-ean"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kategori</Label>
                <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={categoryOpen}
                      className="w-full justify-between font-normal"
                    >
                      {createProductForm.kategori || "Välj kategori..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Sök kategori..." />
                      <CommandList className="max-h-48">
                        <CommandEmpty>Ingen kategori hittad</CommandEmpty>
                        <CommandGroup>
                          {categories.map((cat) => (
                            <CommandItem
                              key={cat}
                              value={cat}
                              onSelect={() => {
                                setCreateProductForm(prev => ({ ...prev, kategori: cat }));
                                setCategoryOpen(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", createProductForm.kategori === cat ? "opacity-100" : "opacity-0")} />
                              {cat}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Tillverkare</Label>
                <Popover open={manufacturerOpen} onOpenChange={setManufacturerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={manufacturerOpen}
                      className="w-full justify-between font-normal"
                    >
                      {createProductForm.tillverkare || "Välj tillverkare..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Sök tillverkare..." />
                      <CommandList className="max-h-48">
                        <CommandEmpty>Ingen tillverkare hittad</CommandEmpty>
                        <CommandGroup>
                          {manufacturers.map((man) => (
                            <CommandItem
                              key={man}
                              value={man}
                              onSelect={() => {
                                setCreateProductForm(prev => ({ ...prev, tillverkare: man }));
                                setManufacturerOpen(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", createProductForm.tillverkare === man ? "opacity-100" : "opacity-0")} />
                              {man}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateProduct(false)}>
              Avbryt
            </Button>
            <Button 
              onClick={handleCreateProduct} 
              disabled={creatingProduct || !createProductForm.artikelnummer || !createProductForm.ean}
              className="gap-2"
              data-testid="confirm-create-product-btn"
            >
              {creatingProduct ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Skapar...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Skapa produkt
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Price Dialog */}
      <Dialog open={showAddPriceDialog} onOpenChange={setShowAddPriceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Lägg till pris</DialogTitle>
            <DialogDescription>
              Sök efter en produkt och välj leverantör
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Product display */}
            <div className="space-y-2">
              <Label>Produkt *</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={missingPriceProduct?.artikelnummer || ""}
                  readOnly
                  className="pl-10 bg-muted"
                />
              </div>
              {missingPriceProduct && (
                <div className="bg-accent/50 border border-border rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{missingPriceProduct.artikelnummer}</p>
                    <p className="text-sm text-muted-foreground">{missingPriceProduct.ean}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowAddPriceDialog(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            
            {/* Supplier selection */}
            <div className="space-y-2">
              <Label>Leverantör *</Label>
              <Select 
                value={addPriceForm.supplier_id} 
                onValueChange={(value) => {
                  // Update currency to match supplier's default
                  const selectedSupplier = suppliers.find(s => s.id === value);
                  const defaultCurrency = selectedSupplier?.default_currency || "SEK";
                  setAddPriceForm(prev => ({ 
                    ...prev, 
                    supplier_id: value,
                    currency: defaultCurrency
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Välj leverantör..." />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map(supplier => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.company_name}
                      {supplier.default_currency && supplier.default_currency !== "SEK" && (
                        <span className="text-muted-foreground ml-2">({supplier.default_currency})</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Price and Currency */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Inköpspris *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  value={addPriceForm.price}
                  onChange={(e) => setAddPriceForm(prev => ({ ...prev, price: e.target.value }))}
                  data-testid="add-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Valuta</Label>
                <Select 
                  value={addPriceForm.currency} 
                  onValueChange={(value) => setAddPriceForm(prev => ({ ...prev, currency: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SEK">SEK</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="DKK">DKK</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Chemical Tax */}
            <div className="space-y-2">
              <Label>Kem. skatt (SEK per st)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0"
                value={addPriceForm.chemical_tax}
                onChange={(e) => setAddPriceForm(prev => ({ ...prev, chemical_tax: e.target.value }))}
                data-testid="add-chemical-tax-input"
              />
              <p className="text-xs text-muted-foreground">
                Kemikalieskatt per styck (valfritt)
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPriceDialog(false)}>
              Avbryt
            </Button>
            <Button 
              onClick={handleSavePrice}
              disabled={savingPrice || !addPriceForm.supplier_id || !addPriceForm.price}
              className="gap-2"
              data-testid="save-price-btn"
            >
              {savingPrice ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sparar...
                </>
              ) : (
                "Spara pris"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
